import { isAxiosError } from "axios";

import { defaultShouldRetry } from "@/utils/react-query";

export const is404 = (err: Error) => isAxiosError(err) && err.status === 404;

export type RetryCallback = (
  error: Error,
  failureCount: number,
  lastDelay: number,
) => [shouldRetry: boolean, delay: number];

const defaultRetryCallback: RetryCallback = (
  error,
  failureCount,
  lastDelay,
) => [defaultShouldRetry(failureCount, error), lastDelay * 2];

export const requestWithRetry = async <T>(
  requestFn: () => Promise<T>,
  shouldRetryCallback: RetryCallback = defaultRetryCallback,
  initialDelayMS = 250,
) => {
  const requestWithRetry = async (
    attempt: number,
    delay: number,
  ): Promise<T> => {
    try {
      return await requestFn();
    } catch (error) {
      const parsedError =
        error instanceof Error ? error : new Error("Unknown Error");
      const [shouldRetry, nextDelay] = shouldRetryCallback(
        parsedError,
        attempt,
        delay,
      );

      if (!shouldRetry) {
        throw error;
      }

      // Delay re-request until the given delay time has passed
      await new Promise((resolve) => setTimeout(resolve, delay));

      return requestWithRetry(attempt + 1, nextDelay);
    }
  };

  return requestWithRetry(1, initialDelayMS);
};
